import { useState, useEffect } from "react";
import {
	property_type,
	ABN_STATUS_REGISTERED,
	ABN_STATUS_UNREGISTERED,
	LOAN_TYPE_BUSINESS_VEHICLE,
	LOAN_TYPE_BUSINESS_EQUIPMENT,
	LOAN_TYPE_BUSINESS_TRUCK,
	LOAN_TYPE_BUSINESS_WORKING_CAPITAL,
	LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/forms/context";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import clsx from "clsx";
import ABNInput from "@/components/forms/parts/ABNInput";
import ToggleSwitch from "@/components/ui/toggleSwitch";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { secureInput } from "@/lib/security";

export const LoanDetails = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const {
		selectedIndex,
		enabledFields,
		setFormError,
		setValidateFormDetails,
		formContainer,
		toggleFranchise,
		setToggleFranchise,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [toggle, setToggle] = useState(toggleFranchise); // Toggle Switch for Running a Franchise

	const {
		loanType,
		businessAbnStatus,
		businessName,
		abnYears,
		abnMonths,
		trusteeName,
		abn,
		trusteeClassification,
		runningFranchise,
		franchiseBrand,
		postcode,
		businessState,
		purchaseStage,
		assetType,
		assetAge,
		otherAssetType,
		purchaseFrom,
		financeReason,
		otherFinanceReason,
		assetCost,
		deposit,
		propertyCategory,
		propertyOption,
		// propertyLocation,
		propertyAddress,
		rentalIncome,
		propertyValuation,
		assessedValue,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		businessAbnStatus: false,
		businessName: false,
		abnYears: false,
		abnMonths: false,
		trusteeName: false,
		abn: false,
		trusteeClassification: false,
		runningFranchise: false,
		franchiseBrand: false,
		postcode: false,
		businessState: false,
		purchaseStage: false,
		assetType: false,
		assetAge: false,
		purchaseFrom: false,
		otherAssetType: false,
		financeReason: false,
		otherFinanceReason: false,
		assetCost: false,
		deposit: false,
		propertyCategory: false,
		propertyOption: false,
		// propertyLocation: false,
		propertyAddress: false,
		rentalIncome: false,
		propertyValuation: false,
		assessedValue: false,
	});

	useEffect(() => {
		setToggleFranchise(toggle);
		setFormValue("runningFranchise", toggle ? "Yes" : "No");

		if (!toggle) {
			setFormValue("franchiseBrand", "");
		}
	}, [toggle]);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const registered = businessAbnStatus === ABN_STATUS_REGISTERED;
		const unregistered = businessAbnStatus === ABN_STATUS_UNREGISTERED;
		const isWorkingCapital = loanType === LOAN_TYPE_BUSINESS_WORKING_CAPITAL;
		const isCommercial = loanType === LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY;

		const newErrors = {
			businessAbnStatus: isWorkingCapital ? !businessAbnStatus : false,
			businessName: isWorkingCapital ? !businessName : false,
			abnYears: isWorkingCapital && registered && !abnYears,
			abnMonths: isWorkingCapital && registered && !abnMonths,
			trusteeName: isWorkingCapital && registered && !trusteeName,
			abn: isWorkingCapital && registered && !abn,
			trusteeClassification: isWorkingCapital && registered && !trusteeClassification,
			runningFranchise: isWorkingCapital && registered && !runningFranchise,
			franchiseBrand:
				isWorkingCapital && registered && runningFranchise === "Yes"
					? !franchiseBrand
					: false,
			postcode: isWorkingCapital && unregistered ? !postcode : false,
			businessState: isWorkingCapital && unregistered ? !businessState : false,
			purchaseStage: [
				LOAN_TYPE_BUSINESS_VEHICLE,
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
			].includes(loanType)
				? !purchaseStage
				: false,
			assetType: [
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
				LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY,
			].includes(loanType)
				? !assetType
				: false,
			assetAge: [
				LOAN_TYPE_BUSINESS_VEHICLE,
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
			].includes(loanType)
				? !assetAge
				: false,
			purchaseFrom: [
				LOAN_TYPE_BUSINESS_VEHICLE,
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
			].includes(loanType)
				? !purchaseFrom
				: false,
			otherAssetType: assetType.toLowerCase().includes("other") ? !otherAssetType : false,
			financeReason: loanType === LOAN_TYPE_BUSINESS_EQUIPMENT && !financeReason,
			otherFinanceReason: financeReason.toLowerCase().includes("other")
				? !otherFinanceReason
				: false,
			assetCost: isCommercial && !assetCost,
			deposit: isCommercial && !deposit,
			propertyCategory: isCommercial && !propertyCategory,
			propertyOption: isCommercial && !propertyOption,
			// propertyLocation: isCommercial && !propertyLocation,
			propertyAddress: isCommercial && !propertyAddress,
			rentalIncome: isCommercial && !rentalIncome,
			propertyValuation: isCommercial && !propertyValuation,
			assessedValue: isCommercial && propertyValuation === "Completed" && !assessedValue,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);
		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [...Object.values(formValues)]);

	const resetFormValues = (businessAbnStatus?: string) => {
		if (businessAbnStatus === ABN_STATUS_REGISTERED) {
			setFormValue("businessState", "");
			setFormValue("postcode", "");
		} else if (businessAbnStatus === ABN_STATUS_UNREGISTERED) {
			setFormValue("abnYears", "");
			setFormValue("abnMonths", "");
			setFormValue("trusteeName", "");
			setFormValue("abn", "");
			setFormValue("trusteeClassification", "");
			setFormValue("franchiseBrand", "");
			setFormValue("runningFranchise", "");
			setToggle(false);
		} else {
			setFormValue("businessAbnStatus", "");
			setFormValue("businessName", "");
			setFormValue("businessState", "");
			setFormValue("postcode", "");
			setFormValue("abnYears", "");
			setFormValue("abnMonths", "");
			setFormValue("trusteeName", "");
			setFormValue("abn", "");
			setFormValue("trusteeClassification", "");
			setFormValue("franchiseBrand", "");
			setFormValue("runningFranchise", "");
			setToggle(false);
		}
	};

	const handleInputPostcodeChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: keyof typeof formValues
	) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.postcode(value);
		setFormValue(field, sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, [field]: !sanitizedValue.isValid }));
	};

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: keyof typeof formValues
	) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.textarea(value);
		setFormValue(field, sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, [field]: !sanitizedValue.isValid }));
	};

	const handleSelectChange = (
		e: React.ChangeEvent<HTMLSelectElement> | string,
		field: keyof typeof formValues
	) => {
		setFormValue(field, typeof e === "string" ? e : e.target.value);
		setErrors((prev) => ({ ...prev, [field]: typeof e === "string" ? !e : !e.target.value }));
	};

	const handleInputNumberChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		field: keyof typeof formValues
	) => {
		let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
		setFormValue(field, formattedValue.toLocaleString());
		setErrors((prev) => ({
			...prev,
			[field]: formattedValue < 10000,
		}));
	};

	return (
		<div className="bizF-space-y-2 bizF-m-2 bizF-pb-4">
			{/* Loan Type: Commercial Property Finance */}
			{loanType === LOAN_TYPE_BUSINESS_COMMERCIAL_PROPERTY && (
				<div className="bizF-flex bizF-flex-col bizF-gap-y-2">
					<label className="bizF-label">
						Tell us about the property you want to finance
					</label>

					<div className="bizF-flex-1">
						<label className="bizF-label">What is the expected purchase price?</label>
						<div className="bizF-flex-1 bizF-relative">
							<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
								<span className="bizF-text-gray-500 bizF-text-md">$</span>
							</div>
							<input
								value={assetCost}
								onChange={(e) => handleInputNumberChange(e, "assetCost")}
								aria-label="Asset Cost"
								placeholder={"Asset Cost"}
								type="text"
								id="assetCost"
								aria-describedby="Asset Cost"
								inputMode="numeric"
								className={clsx(
									errors.assetCost ? "bizF-input-error" : "bizF-input-focus",
									"bizF-input-default bizF-pl-8 bizF-pr-12"
								)}
							/>
							<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-right-0 bizF-flex bizF-items-center bizF-pr-3">
								<span className="bizF-text-gray-500 sm:bizF-text-md">AUD</span>
							</div>
						</div>
						{errors.assetCost && (
							<span className="bizF-text-xs bizF-text-red-500">
								Minimum asset cost is $10,000
							</span>
						)}
					</div>

					<div className="bizF-flex-1">
						<label className="bizF-label">How much deposit do you have?</label>
						<div className="bizF-flex-1 bizF-relative">
							<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
								<span className="bizF-text-gray-500 bizF-text-md">$</span>
							</div>
							<input
								value={deposit}
								onChange={(e) => handleInputNumberChange(e, "deposit")}
								aria-label="Deposit"
								placeholder={"Deposit"}
								type="text"
								id="deposit"
								aria-describedby="Deposit"
								inputMode="numeric"
								className={clsx(
									errors.deposit ? "bizF-input-error" : "bizF-input-focus",
									"bizF-input-default bizF-pl-8 bizF-pr-12"
								)}
							/>
							<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-right-0 bizF-flex bizF-items-center bizF-pr-3">
								<span className="bizF-text-gray-500 sm:bizF-text-md">AUD</span>
							</div>
						</div>
						{errors.deposit && (
							<span className="bizF-text-xs bizF-text-red-500">
								Minimum deposit is $10,000
							</span>
						)}
					</div>

					<div className="bizF-flex-1">
						<label className="bizF-label">What is the property type?</label>
						<div className="bizF-flex bizF-items-center bizF-space-x-2">
							<select
								value={assetType}
								onChange={(e) => {
									handleSelectChange(e, "assetType");
									setFormValue("propertyCategory", "");
									setFormValue("propertyOption", "");
								}}
								id="propertyAssetType"
								aria-label="Asset Type"
								className={clsx(
									errors.assetType ? "bizF-select-error" : "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.assetType?.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.assetType && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>

					{assetType && (
						<AnimateChangeInHeight className="bizF-overflow-visible">
							<div className="bizF-flex-1">
								<label className="bizF-label">
									What is the asset classification?
								</label>
								<div className="bizF-flex bizF-items-center bizF-space-x-2">
									<select
										value={propertyCategory}
										onChange={(e) => {
											handleSelectChange(e, "propertyCategory");
											setFormValue("propertyOption", "");
										}}
										id="propertyCategory"
										aria-label="Property Category"
										className={clsx(
											errors.propertyCategory
												? "bizF-select-error"
												: "bizF-select-focus",
											"bizF-select-default"
										)}
									>
										<option value="" disabled>
											Please select...
										</option>
										{property_type
											.find((o) => o.value === assetType)
											?.property_subtypes?.map(
												(option: { value: string; option: string }) => (
													<option key={option.value} value={option.value}>
														{option.value}
													</option>
												)
											)}
									</select>
								</div>
								{errors.propertyCategory && (
									<span className="bizF-text-red-500 bizF-text-sm">
										This field is required
									</span>
								)}
							</div>
						</AnimateChangeInHeight>
					)}

					{propertyCategory && (
						<AnimateChangeInHeight className="bizF-overflow-visible">
							<div className="bizF-flex-1">
								<label className="bizF-label">What type of property?</label>
								<div className="bizF-flex bizF-items-center bizF-space-x-2">
									{propertyCategory &&
									propertyCategory.toLowerCase().trim() === "other" ? (
										<div className="bizF-flex-1">
											<input
												value={propertyOption}
												onChange={(e) => {
													handleInputChange(e, "propertyOption");
												}}
												aria-label="Property Option"
												type="text"
												id="propertyOptionInput"
												placeholder="Specify other option"
												autoComplete="true"
												maxLength={50}
												className={clsx(
													errors.propertyOption
														? "bizF-input-error"
														: "bizF-input-focus",
													"bizF-input-default"
												)}
											/>
										</div>
									) : (
										<select
											value={propertyOption}
											onChange={(e) => {
												handleSelectChange(e, "propertyOption");
											}}
											id="propertyOptionSelect"
											aria-label="Property Option"
											className={clsx(
												errors.propertyOption
													? "bizF-select-error"
													: "bizF-select-focus",
												"bizF-select-default"
											)}
										>
											<option value="" disabled>
												Please select...
											</option>
											{property_type
												.find((o) => o.value === assetType)
												?.property_subtypes?.find(
													(o) => o.value === propertyCategory
												)
												?.property_options?.map(
													(option: { value: string; option: string }) => (
														<option
															key={option.value}
															value={option.value}
														>
															{option.value}
														</option>
													)
												)}
										</select>
									)}
								</div>
								{errors.propertyOption && (
									<span className="bizF-text-red-500 bizF-text-sm">
										This field is required
									</span>
								)}
							</div>
						</AnimateChangeInHeight>
					)}

					{/* <div className="bizF-flex-1">
						<RadioGroupField
							id="propertyLocation"
							label="Where is the property located?"
							value={propertyLocation || ""}
							options={enabledFields.propertyLocation?.map((item) => ({
								id: item.id,
								value: item.value,
								label: item.value,
								icon:
									item.value == "Metro" ? (
										<CityIcon size={24} weight="duotone" />
									) : item.value == "Regional" ? (
										<BuildingIcon size={24} weight="duotone" />
									) : item.value == "Rural" ? (
										<TreeIcon size={24} weight="duotone" />
									) : null,
							}))}
							onChange={(value) => {
								handleSelectChange(value, "propertyLocation");
							}}
							required={true}
							error={errors.propertyLocation ? "This field is required" : ""}
							disabled={false}
							className={""}
							gridCols={3}
							showIcons={true}
							disabledOptions={[]}
							navRef={isNavDivSmall}
						/>
					</div>  */}

					<div className="bizF-flex-1">
						<label className="bizF-label">
							If available, what is the property address?
						</label>
						<input
							value={propertyAddress}
							onChange={(e) => {
								handleInputChange(e, "propertyAddress");
							}}
							aria-label="Property Address"
							type="text"
							id="propertyAddress"
							placeholder="Property Address"
							autoComplete="true"
							maxLength={120}
							className={clsx(
								errors.propertyAddress ? "bizF-input-error" : "bizF-input-focus",
								"bizF-input-default"
							)}
						/>
						{errors.propertyAddress && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>

					<div className="bizF-flex-1">
						<RadioGroupField
							id="rentalIncome"
							label="Does/will the property generate rental income?"
							value={rentalIncome || ""}
							options={enabledFields.rentalIncome?.map((item) => ({
								id: item.id,
								value: item.value,
								label: item.value,
								icon: null,
							}))}
							onChange={(value) => {
								handleSelectChange(value, "rentalIncome");
							}}
							required={true}
							error={errors.rentalIncome ? "This field is required" : ""}
							disabled={false}
							className={""}
							gridCols={2}
							showIcons={false}
							disabledOptions={[]}
							navRef={isNavDivSmall}
						/>
					</div>

					<div className="bizF-flex-1">
						<label className="bizF-label">
							Has a formal property valuation been completed?
						</label>
						<div className="bizF-flex bizF-items-center bizF-space-x-2">
							<select
								value={propertyValuation}
								onChange={(e) => {
									handleSelectChange(e, "propertyValuation");
									setFormValue("assessedValue", "");
								}}
								id="propertyValuation"
								aria-label="Property Valuation"
								className={clsx(
									errors.propertyValuation
										? "bizF-select-error"
										: "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.propertyValuation?.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.propertyValuation && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>

					{propertyValuation === "Completed" && (
						<div className="bizF-flex-1">
							<label className="bizF-label">
								What was the assessed value of the property?
							</label>
							<div className="bizF-flex-1 bizF-relative">
								<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-left-0 bizF-flex bizF-items-center bizF-pl-3">
									<span className="bizF-text-gray-500 bizF-text-md">$</span>
								</div>
								<input
									value={assessedValue}
									onChange={(e) => handleInputNumberChange(e, "assessedValue")}
									aria-label="Assessed Value"
									placeholder={"Assessed Value"}
									type="text"
									id="assessedValue"
									aria-describedby="Assessed Value"
									inputMode="numeric"
									className={clsx(
										errors.assessedValue
											? "bizF-input-error"
											: "bizF-input-focus",
										"bizF-input-default bizF-pl-8 bizF-pr-12"
									)}
								/>
								<div className="bizF-pointer-events-none bizF-absolute bizF-inset-y-0 bizF-right-0 bizF-flex bizF-items-center bizF-pr-3">
									<span className="bizF-text-gray-500 sm:bizF-text-md">AUD</span>
								</div>
							</div>
							{errors.assessedValue && (
								<span className="bizF-text-xs bizF-text-red-500">
									Minimum value is $10,000
								</span>
							)}
						</div>
					)}
				</div>
			)}
			{/* End of Loan Type: Commercial Property Finance */}

			{/* Loan Type: Working Capital/Cash Flow */}
			{loanType === LOAN_TYPE_BUSINESS_WORKING_CAPITAL && (
				<>
					<div className="bizF-flex-1">
						<label className="bizF-label">What is your current ABN status?</label>
						<div className="bizF-flex bizF-items-center bizF-space-x-2">
							<select
								value={businessAbnStatus}
								onChange={(e) => {
									handleSelectChange(e, "businessAbnStatus");
									resetFormValues(e.target.value);
								}}
								id="businessAbnStatus"
								aria-label="ABN Status"
								className={clsx(
									errors.businessAbnStatus
										? "bizF-select-error"
										: "bizF-select-focus",
									"bizF-select-default"
								)}
							>
								<option value="" disabled>
									Please select...
								</option>
								{enabledFields.businessAbnStatus?.map((option) => (
									<option key={option.value} value={option.value}>
										{option.value}
									</option>
								))}
							</select>
						</div>
						{errors.businessAbnStatus && (
							<span className="bizF-text-red-500 bizF-text-sm">
								This field is required
							</span>
						)}
					</div>

					<div className="bizF-flex-1">
						<label className="bizF-label">
							What is the name associated with your venture?
						</label>
						<input
							value={businessName}
							onChange={(e) => {
								handleInputChange(e, "businessName");
							}}
							aria-label="Business Name"
							type="text"
							id="businessName"
							placeholder="Business Name"
							autoComplete="true"
							maxLength={50}
							className={clsx(
								errors.businessName ? "bizF-input-error" : "bizF-input-focus",
								"bizF-input-default"
							)}
						/>
						{errors.businessName && (
							<span className="bizF-text-sm bizF-text-red-500">
								This field is required
							</span>
						)}
					</div>

					{businessAbnStatus === ABN_STATUS_UNREGISTERED && (
						<>
							<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
								<div className="bizF-flex-1">
									<label className="bizF-label">State of Operation</label>
									<select
										value={businessState}
										onChange={(e) => {
											handleSelectChange(e, "businessState");
										}}
										id="businessState"
										aria-label="Business State"
										className={clsx(
											errors.businessState
												? "bizF-select-error"
												: "bizF-select-focus",
											"bizF-select-default"
										)}
									>
										<option value="" disabled>
											Please select a state...
										</option>
										{enabledFields.businessState?.map((option) => (
											<option key={option.value} value={option.value}>
												{option.value}
											</option>
										))}
									</select>
									{errors.businessState && (
										<span className="bizF-text-red-500 bizF-text-sm">
											This field is required
										</span>
									)}
								</div>

								<div className="bizF-flex-1">
									<label className="bizF-label">Postcode</label>
									<input
										value={postcode}
										onChange={(e) => {
											handleInputPostcodeChange(e, "postcode");
										}}
										aria-label="Postcode"
										type="text"
										id="postcode"
										placeholder="Postcode"
										autoComplete="true"
										className={clsx(
											errors.postcode
												? "bizF-input-error"
												: "bizF-input-focus",
											"bizF-input-default"
										)}
									/>
									{errors.postcode && (
										<span className="bizF-text-sm bizF-text-red-500">
											This field is required
										</span>
									)}
								</div>
							</div>
						</>
					)}

					{businessAbnStatus === ABN_STATUS_REGISTERED && (
						<>
							<div className={clsx("bizF-flex bizF-flex-col bizF-gap-x-3")}>
								<label className="bizF-label">
									How long has your ABN been running?
								</label>
								<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
									<div className="bizF-flex-1">
										<select
											value={abnYears}
											onChange={(e) => {
												handleSelectChange(e, "abnYears");
											}}
											id="abnYears"
											aria-label="ABN Years"
											className={clsx(
												errors.abnYears
													? "bizF-select-error"
													: "bizF-select-focus",
												"bizF-select-default"
											)}
										>
											<option value="" disabled>
												Please select years...
											</option>
											{enabledFields.abnYears?.map((option) => (
												<option key={option} value={option}>
													{option}
												</option>
											))}
										</select>
										{errors.abnYears && (
											<span className="bizF-text-red-500 bizF-text-sm">
												This field is required
											</span>
										)}
									</div>

									<div className="bizF-flex-1">
										<select
											value={abnMonths}
											onChange={(e) => {
												handleSelectChange(e, "abnMonths");
											}}
											id="abnMonths"
											aria-label="ABN Months"
											className={clsx(
												errors.abnMonths
													? "bizF-select-error"
													: "bizF-select-focus",
												"bizF-select-default"
											)}
										>
											<option value="" disabled>
												Please select months...
											</option>
											{enabledFields.abnMonths?.map((option) => (
												<option key={option} value={option}>
													{option}
												</option>
											))}
										</select>
										{errors.abnMonths && (
											<span className="bizF-text-red-500 bizF-text-sm">
												This field is required
											</span>
										)}
									</div>
								</div>
							</div>

							<div className="bizF-flex-1">
								<label className="bizF-label">
									What is the name of the trustee?
								</label>
								<input
									value={trusteeName}
									onChange={(e) => {
										handleInputChange(e, "trusteeName");
									}}
									aria-label="Trustee Name"
									type="text"
									id="trusteeName"
									placeholder="Trustee Name"
									autoComplete="true"
									maxLength={50}
									className={clsx(
										errors.trusteeName
											? "bizF-input-error"
											: "bizF-input-focus",
										"bizF-input-default"
									)}
								/>
								{errors.trusteeName && (
									<span className="bizF-text-sm bizF-text-red-500">
										This field is required
									</span>
								)}
							</div>

							<div className={clsx("bizF-flex bizF-flex-col bizF-gap-x-3")}>
								<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
									<div className="bizF-flex-1">
										<label className="bizF-label">
											What is the trust's ABN?
										</label>
										<ABNInput
											value={abn}
											error={errors.abn}
											onChange={(val) => {
												setFormValue("abn", val);
												setErrors((prev) => ({ ...prev, abn: !val }));
											}}
										/>
									</div>

									<div className="bizF-flex-1">
										<label className="bizF-label">
											What is the trustee's classification?
										</label>
										<select
											value={trusteeClassification}
											onChange={(e) => {
												handleSelectChange(e, "trusteeClassification");
											}}
											id="trusteeClassification"
											aria-label="Trustee Classification"
											className={clsx(
												errors.trusteeClassification
													? "bizF-select-error"
													: "bizF-select-focus",
												"bizF-select-default"
											)}
										>
											<option value="" disabled>
												Please select...
											</option>
											{enabledFields.trusteeClassification?.map((option) => (
												<option key={option.value} value={option.value}>
													{option.value}
												</option>
											))}
										</select>
										{errors.trusteeClassification && (
											<span className="bizF-text-red-500 bizF-text-sm">
												This field is required
											</span>
										)}
									</div>
								</div>
							</div>

							<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
								<div
									id="franchise-toggle"
									className={clsx(
										"bizF-flex-1 bizF-my-2 md:bizF-my-0 md:bizF-mt-2 bizF-flex bizF-items-center bizF-gap-x-2"
									)}
									tabIndex={-1}
								>
									<label className="bizF-font-semibold bizF-text-base bizF-leading-6 bizF-text-gray-900">
										Are you running a franchise?
									</label>
									<ToggleSwitch
										label=""
										enabled={toggle}
										setEnabled={setToggle}
										displayLabel={false}
									/>
								</div>
								<div className="bizF-flex-1">
									{toggle && (
										<AnimateChangeInHeight className="bizF-overflow-visible">
											<>
												<label className="bizF-label">
													What is the franchise brand name?
												</label>
												<input
													value={franchiseBrand}
													onChange={(e) => {
														handleInputChange(e, "franchiseBrand");
													}}
													aria-label="Franchise Brand Name"
													type="text"
													id="franchiseBrand"
													placeholder="Franchise Brand Name"
													autoComplete="true"
													maxLength={50}
													className={clsx(
														errors.franchiseBrand
															? "bizF-input-error"
															: "bizF-input-focus",
														"bizF-input-default"
													)}
												/>
												{errors.franchiseBrand && (
													<span className="bizF-text-sm bizF-text-red-500">
														This field is required
													</span>
												)}
											</>
										</AnimateChangeInHeight>
									)}
								</div>
							</div>
						</>
					)}
				</>
			)}
			{/* End of Loan Type: Working Capital/Cash Flow */}

			{[
				LOAN_TYPE_BUSINESS_VEHICLE,
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
			].includes(loanType) && (
				<>
					<RadioGroupField
						id="purchaseStage"
						label="Have you identified the asset that you intent to acquire?"
						value={purchaseStage || ""}
						options={enabledFields.purchaseStage?.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							handleSelectChange(value, "purchaseStage");
						}}
						required={true}
						error={errors.purchaseStage ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}

			{[LOAN_TYPE_BUSINESS_EQUIPMENT, LOAN_TYPE_BUSINESS_TRUCK].includes(loanType) && (
				<div className="bizF-flex bizF-flex-col">
					<label className="bizF-label">What kind of asset do you plan to buy?</label>

					<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
						<div className="bizF-flex-1">
							<div className="bizF-flex bizF-items-center bizF-space-x-2">
								<select
									value={assetType}
									onChange={(e) => {
										handleSelectChange(e, "assetType");
										if (!e.target.value.toLowerCase().includes("other")) {
											setFormValue("otherAssetType", "");
										}
									}}
									id="assetType"
									aria-label="Asset Type"
									className={clsx(
										errors.assetType
											? "bizF-select-error"
											: "bizF-select-focus",
										"bizF-select-default"
									)}
								>
									<option value="" disabled>
										Please select...
									</option>
									{enabledFields.assetType?.map((option) => (
										<option key={option.value} value={option.value}>
											{option.value}
										</option>
									))}
								</select>
							</div>
							{errors.assetType && (
								<span className="bizF-text-red-500 bizF-text-sm">
									This field is required
								</span>
							)}
						</div>

						{assetType.toLowerCase().includes("other") && (
							<div className="bizF-flex-1">
								<AnimateChangeInHeight className="bizF-overflow-visible">
									<input
										value={otherAssetType}
										onChange={(e) => {
											handleInputChange(e, "otherAssetType");
										}}
										aria-label="Specify other asset type"
										type="text"
										id="otherAssetType"
										placeholder="Specify other asset type"
										autoComplete="true"
										maxLength={50}
										className={clsx(
											errors.otherAssetType
												? "bizF-input-error"
												: "bizF-input-focus",
											"bizF-input-default"
										)}
									/>
									{errors.otherAssetType && (
										<span className="bizF-text-sm bizF-text-red-500">
											This field is required
										</span>
									)}
								</AnimateChangeInHeight>
							</div>
						)}
					</div>
				</div>
			)}

			{[LOAN_TYPE_BUSINESS_EQUIPMENT].includes(loanType) && (
				<div className="bizF-flex bizF-flex-col">
					<label className="bizF-label">
						What is the primary reason for seeking equipment finance?
					</label>

					<div className="bizF-flex bizF-flex-col bizF-gap-y-2 md:bizF-flex-row md:bizF-gap-x-2">
						<div className="bizF-flex-1">
							<div className="bizF-flex bizF-items-center bizF-space-x-2">
								<select
									value={financeReason}
									onChange={(e) => {
										handleSelectChange(e, "financeReason");
										if (!e.target.value.toLowerCase().includes("other")) {
											setFormValue("otherFinanceReason", "");
										}
									}}
									id="financeReason"
									aria-label="Finance Reason"
									className={clsx(
										errors.financeReason
											? "bizF-select-error"
											: "bizF-select-focus",
										"bizF-select-default"
									)}
								>
									<option value="" disabled>
										Please select...
									</option>
									{enabledFields.financeReason?.map((option) => (
										<option key={option.value} value={option.value}>
											{option.value}
										</option>
									))}
								</select>
							</div>
							{errors.financeReason && (
								<span className="bizF-text-red-500 bizF-text-sm">
									This field is required
								</span>
							)}
						</div>

						{financeReason.toLowerCase().includes("other") && (
							<div className="bizF-flex-1">
								<AnimateChangeInHeight className="bizF-overflow-visible">
									<input
										value={otherFinanceReason}
										onChange={(e) => {
											handleInputChange(e, "otherFinanceReason");
										}}
										aria-label="Specify reason for finance"
										type="text"
										id="otherFinanceReason"
										placeholder="Specify reason for finance"
										autoComplete="true"
										maxLength={50}
										className={clsx(
											errors.otherFinanceReason
												? "bizF-input-error"
												: "bizF-input-focus",
											"bizF-input-default"
										)}
									/>
									{errors.otherFinanceReason && (
										<span className="bizF-text-sm bizF-text-red-500">
											This field is required
										</span>
									)}
								</AnimateChangeInHeight>
							</div>
						)}
					</div>
				</div>
			)}

			{[
				LOAN_TYPE_BUSINESS_VEHICLE,
				LOAN_TYPE_BUSINESS_EQUIPMENT,
				LOAN_TYPE_BUSINESS_TRUCK,
			].includes(loanType) && (
				<>
					<RadioGroupField
						id="assetAge"
						label="In what state or condition is the asset?"
						value={assetAge || ""}
						options={enabledFields.assetAge?.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							handleSelectChange(value, "assetAge");
						}}
						required={true}
						error={errors.assetAge ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>

					<RadioGroupField
						id="purchaseFrom"
						label="Which seller type would you prefer?"
						value={purchaseFrom || ""}
						options={enabledFields.purchaseFrom?.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							handleSelectChange(value, "purchaseFrom");
						}}
						required={true}
						error={errors.purchaseFrom ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={4}
						showIcons={true}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}
		</div>
	);
};
